import type { FC } from 'react'
import React, { useCallback, useContext, useEffect, useRef } from 'react'

import { ChildRegistrationContext } from './constants'

// Utility hook used to register/keep track of elements that should be treated as 'nested children'
// a.k.a not trigger `onClickOutside` when clicked, even if they aren't dom descendants
// of the target element. Mostly used for popovers as they are using a portal and thus aren't
// necessarily a descendant of the target element.

export function useRegisterChildRootContainer<
  T extends HTMLElement = HTMLElement,
>() {
  const ref = useRef<T | null>(null)
  const refCallback = useCallback((el: T | null) => {
    ref.current = el
  }, [])

  const { register, unregister } = useContext(ChildRegistrationContext)

  const refGetters = useRef<Set<() => HTMLElement[]>>(new Set())
  const registerChild = useCallback((getter: () => HTMLElement[]) => {
    refGetters.current.add(getter)
  }, [])
  const unregisterChild = useCallback((getter: () => HTMLElement[]) => {
    refGetters.current.delete(getter)
  }, [])

  const RegistrationContext = useCallback<FC<{ children: React.ReactNode }>>(
    ({ children }) => (
      <ChildRegistrationContext.Provider
        value={{ register: registerChild, unregister: unregisterChild }}
      >
        {children}
      </ChildRegistrationContext.Provider>
    ),
    [registerChild, unregisterChild]
  )

  const getChildRefs = useCallback(
    () =>
      Array.from(refGetters.current, (getter) => getter())
        .flat()
        .concat(ref.current!)
        .filter(Boolean),
    []
  )

  // Register children in the current context to a (potential) parent context
  // to support nesting of child registration contexts
  useEffect(() => {
    register(getChildRefs)
    return () => unregister(getChildRefs)
  }, [getChildRefs, register, unregister])

  return {
    ref: refCallback,
    getChildRefs,
    ChildRegistrationContext: RegistrationContext,
  }
}
